import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';
import React from 'react';
import './clock.css';

// adjustedBy: 'user' = manually changed (cyan), 'cascade' = auto-pushed (amber), null = original
// IN and OUT sit OUTSIDE the -/+ pair, in a fixed box, so both rows line up whatever the word.
const LABEL_STYLE = (color) => ({
    fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px',
    color, opacity: 0.75, lineHeight: 1, display: 'inline-block',
    width: '22px', textAlign: 'right', marginRight: '3px', flex: 'none',
});

const ADJUSTED_USER_COLOR = '#26c6da';
const ADJUSTED_CASCADE_COLOR = '#ffb74d';

const AdjustButton = ({ label, onClick, color, square = false }) => (
    <Tooltip title={label === '-' ? 'Trim 0.1s' : 'Extend 0.1s'} placement="top" arrow>
        <IconButton
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onClick(); }}
            size="small"
            sx={{
                // Identical squares, but only when asked: '-' and '+' are different glyph
                // widths, so the original padding-based sizing produced two different boxes.
                ...(square
                    ? { padding: 0, width: '24px', height: '24px', flex: 'none' }
                    : { padding: '4px 7px', height: '24px' }),
                minWidth: '24px',
                fontSize: '14px',
                fontWeight: 700,
                color,
                lineHeight: 1,
                borderRadius: '5px',
                border: `1px solid ${color}44`,
                '&:hover': { backgroundColor: color + '22', borderColor: color + 'aa' },
            }}
        >
            {label}
        </IconButton>
    </Tooltip>
);

const Clock = ({ header = true, showLabels = false, showInOutLabels = false, squareButtons = false, duration, item, onJumpClick = null, showJump = false, onAdjustTimes = null, adjustedBy = null }) => {
    const theme = useTheme();
    const baseOutColor = header ? theme.palette.warning.main : theme.palette.text.primary;
    const baseInColor = theme.palette.text.primary;

    const timeColor = adjustedBy === 'user'
        ? ADJUSTED_USER_COLOR
        : adjustedBy === 'cascade'
            ? ADJUSTED_CASCADE_COLOR
            : null;

    const inColor = timeColor || baseInColor;
    const outColor = timeColor || baseOutColor;
    // IN and OUT buttons use the same border-visible color so they look symmetrical
    const inBtnColor = timeColor || theme.palette.primary.main;
    const outBtnColor = timeColor || baseOutColor;

    React.useEffect(() => {
        document.documentElement.style.setProperty('--primary-color', theme.palette.primary.main);
        document.documentElement.style.setProperty('--primary-color-hover', theme.palette.primary.main + '20');
        document.documentElement.style.setProperty('--warning-color', baseOutColor);
    }, [theme.palette.primary.main, baseOutColor]);

    return (
        <div className="clock-container">
            {onJumpClick && showJump && (
                <Tooltip title="Find this clip in context — jumps to it in the full transcript and clears search/filters/sort" placement="top" arrow enterDelay={150}>
                    <IconButton onClick={() => onJumpClick(item)} size="small" className="clock-go-button">
                        <CenterFocusStrongIcon className="clock-go-icon" />
                    </IconButton>
                </Tooltip>
            )}

            <div className="clock-display">
                {/* First time value */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                    {showInOutLabels && <span style={LABEL_STYLE(theme.palette.text.disabled)}>In</span>}
                    {onAdjustTimes && <AdjustButton label="-" onClick={() => onAdjustTimes(item, -1, 0)} color={inBtnColor} square={squareButtons} />}
                    {showLabels && (
                        <span style={{ fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.5px', color: theme.palette.text.disabled, marginRight: '2px', lineHeight: 1 }}>List</span>
                    )}
                    <p className="clock-time" style={{ color: inColor }}>{duration[0]}</p>
                    {onAdjustTimes && <AdjustButton label="+" onClick={() => onAdjustTimes(item, 1, 0)} color={inBtnColor} square={squareButtons} />}
                </Box>

                {/* Second time value */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                    {showInOutLabels && <span style={LABEL_STYLE(outColor)}>Out</span>}
                    {onAdjustTimes && <AdjustButton label="-" onClick={() => onAdjustTimes(item, 0, -1)} color={outBtnColor} square={squareButtons} />}
                    {showLabels && (
                        <span style={{ fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.5px', color: outColor, opacity: 0.7, marginRight: '2px', lineHeight: 1 }}>Pick</span>
                    )}
                    <p className="clock-time-colored" style={{ color: outColor }}>{duration[1]}</p>
                    {onAdjustTimes && <AdjustButton label="+" onClick={() => onAdjustTimes(item, 0, 1)} color={outBtnColor} square={squareButtons} />}
                </Box>
            </div>
        </div>
    );
};

export default observer(Clock);
